import { getResolvedWeightWithTheme, Breakpoints, withTheming, themedFontFamily, getFocusStyles, Shape } from '@doordash/design-language';
import styled, { css } from 'styled-components';
import { FieldSizeSemantic } from '../../constants.js';

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
// these style types can be updated after do the v8 legacy Text/Type cleanup
var FONT_STYLES = css(["font-size:", "px;line-height:", "px;letter-spacing:", "ch;font-weight:", ";@media (min-width:", "px){font-size:", "px;line-height:", "px;letter-spacing:", "ch;font-weight:", ";}"], function (props) {
  return props.mobileInputTextStyles.size;
}, function (props) {
  return props.mobileInputTextStyles.lineHeight;
}, function (props) {
  return props.mobileInputTextStyles.letterSpacing;
}, function (props) {
  return getResolvedWeightWithTheme(props.theme, props.mobileInputTextStyles.weight);
}, Breakpoints.Mobile + 1, function (props) {
  return props.desktopInputTextStyles.size;
}, function (props) {
  return props.desktopInputTextStyles.lineHeight;
}, function (props) {
  return props.desktopInputTextStyles.letterSpacing;
}, function (props) {
  return getResolvedWeightWithTheme(props.theme, props.desktopInputTextStyles.weight);
});
var InputRoot = styled.div.withConfig({
  displayName: "Input__InputRoot",
  componentId: "sc-1o75rg4-0"
})(["display:flex;min-height:", "px;"], function (props) {
  return props.themeHeight;
});

var getErrorStyles = function getErrorStyles(props) {
  if (props.error) {
    return css(["background:", ";box-shadow:inset 0 0 0 ", "px ", ";color:", ";"], props.theme.Colors.FieldsBackgroundError, props.theme.BorderWidth.FieldsError, props.theme.Colors.FieldsBorderError, props.theme.Colors.FieldsForegroundError);
  }

  return null;
};

var getBareStyles = function getBareStyles(props) {
  if (props.isBare) {
    return css(["box-shadow:none;&:focus-within{box-shadow:none;}"]);
  }

  return null;
};

var getDisabledStyles = function getDisabledStyles(props) {
  if (props.disabled) {
    return css(["background-color:", ";box-shadow:inset 0 0 0 ", "px ", ";color:", ";cursor:not-allowed;"], props.theme.Colors.FieldsBackgroundDisabled, props.theme.BorderWidth.FieldsDefault, props.theme.Colors.FieldsBorderDisabled, props.theme.Colors.FieldsForegroundDisabled);
  }

  return null;
};

var getPlaceholderStyles = function getPlaceholderStyles(props) {
  var placeholderColor = props.theme.Colors.FieldsForegroundPlaceholder;
  return css(["&::-webkit-input-placeholder{color:", ";}&:-moz-placeholder{color:", ";}&::-moz-placeholder{color:", ";}&:-ms-input-placeholder{color:", ";}&::-ms-input-placeholder{color:", ";}&::placeholder{color:", ";}&:placeholder-shown{text-overflow:ellipsis;}"], placeholderColor, placeholderColor, placeholderColor, placeholderColor, placeholderColor, placeholderColor);
};

var getPadding = function getPadding(_ref) {
  var isRounded = _ref.isRounded,
      isBare = _ref.isBare,
      size = _ref.size,
      theme = _ref.theme,
      props = _objectWithoutProperties(_ref, ["isRounded", "isBare", "size", "theme"]);

  // Fields with the opt-in `isBare` prop set to `true`
  // will not have internal padding applied to the Field.
  if (isBare) {
    return '0px';
  } // If this is a textarea or has custom leading/trailing content, we will add some extra vertical padding


  if (props.renderVerticalPadding && !props.hasDefaultRenderContent) {
    // `isAdjustableHeight` is true when `isMultiLine` === true and the input type is `text` (textarea)
    // Additional vertical padding is needed for correct spacing
    if (props.isAdjustableHeight) {
      // Rounded textareas need extra padding to avoid awkward crops/spacing
      var x = isRounded ? size * 2 : size;
      var y = size;
      return "".concat(y, "px ").concat(x, "px");
    } else {
      // When there is custom leading/trailing content, we need to add some extra padding as a backstop because we
      // don't know what is going to be rendered here. Otherwise, custom content that makes the input grow in size could
      // push right against the edge of the input. This keeps things looking nice in those scenarios
      return "".concat(size / 2, "px ").concat(isRounded ? size + theme.Spacing.XxxSmall : size, "px");
    }
  } // The fallback behavior for any Fields that don't meet the above criteria
  // is to only contain horizontal padding aligned with the Field's `size`
  // prop. This ensures that the default height for the Field is equal to the
  // size defined in the theme.


  return "0px ".concat(isRounded ? size + theme.Spacing.XxxSmall : size, "px");
};

var getBorderRadius = function getBorderRadius(props) {
  var borderRadiusVal = props.isRounded ? props.isAdjustableHeight ? props.theme.Size[FieldSizeSemantic[props.size]] : props.theme.Size[FieldSizeSemantic[props.size]] / 2 : props.theme.BorderRadius.FieldsInputDefault;
  return "".concat(props.roundedAbove && props.roundedLeft ? borderRadiusVal : 0, "px ").concat(props.roundedAbove && props.roundedRight ? borderRadiusVal : 0, "px ").concat(props.roundedBelow && props.roundedRight ? borderRadiusVal : 0, "px ").concat(props.roundedBelow && props.roundedLeft ? borderRadiusVal : 0, "px");
};

var getContainerFocusStyles = function getContainerFocusStyles(__focusBehavior) {
  var getDefaults = function getDefaults(props) {
    return css(["background-color:", ";color:", ";", ";"], props.theme.Colors.FieldsBackgroundActive, function (props) {
      return props.theme.Colors.FieldsForegroundActive;
    }, function (props) {
      return getFocusStyles({
        theme: props.theme,
        focusColor: props.theme.Colors.FieldsBorderActive,
        shouldBeInset: true,
        shouldUseFocusRing: false,
        focusWidth: props.theme.BorderWidth.FieldsFocus
      });
    });
  };

  return function (props) {
    if (__focusBehavior === 'disable') {
      return "";
    } else if (__focusBehavior === 'force') {
      return getDefaults(props);
    } else {
      return css(["&:focus-within{", "}"], getDefaults(props));
    }
  };
};

var InputContainer = withTheming(styled.div.withConfig({
  displayName: "Input__InputContainer",
  componentId: "sc-1o75rg4-1"
})(["", " font-family:", ";color:", ";display:flex;align-items:center;width:100%;border-radius:", ";z-index:1;padding:", ";border:none;box-shadow:inset 0 0 0 ", "px ", ";background-color:", ";", " ", " ", " ", ""], FONT_STYLES, function (props) {
  return themedFontFamily(props.theme.FontFamily);
}, function (props) {
  return props.theme.Colors.FieldsForegroundInactive;
}, getBorderRadius, getPadding, function (props) {
  return props.theme.BorderWidth.FieldsDefault;
}, function (props) {
  return props.theme.Colors.FieldsBorderInactive;
}, function (props) {
  return props.theme.Colors.FieldsBackgroundInactive;
}, function (_ref2) {
  var __focusBehavior = _ref2.__focusBehavior;
  return getContainerFocusStyles(__focusBehavior);
}, getErrorStyles, getBareStyles, getDisabledStyles));
var InputContentContainer = styled.div.withConfig({
  displayName: "Input__InputContentContainer",
  componentId: "sc-1o75rg4-2"
})(["flex-grow:1;background:inherit;max-width:100%;margin:0 ", " 0 auto;"], function (props) {
  return props.hasTrailingRenderContent ? "".concat(props.size, "px") : 'auto';
});

var getDisabledContentStyles = function getDisabledContentStyles(props) {
  if (props.disabled) {
    return css(["cursor:not-allowed;"]);
  }

  return null;
};

var getTextStyles = function getTextStyles(props) {
  if (props.type === 'text' && props.isMultiline) {
    return css(["min-height:", "px;resize:", ";"], (props.numberOfLines || 1) * 24, props.isResizeable ? 'vertical' : 'none');
  }

  return null;
};

var getSelectStyles = function getSelectStyles(props) {
  if (props.tag === 'select') {
    return css(["height:24px;appearance:none;margin-right:-24px;&::-ms-expand{display:none;}"]);
  }

  return null;
};

var InputContent = withTheming(styled.input.withConfig({
  displayName: "Input__InputContent",
  componentId: "sc-1o75rg4-3"
})(["", " font-family:", ";width:100%;border:none;outline:none;flex:1 1 100%;display:flex;background:inherit;color:currentColor;appearance:none;margin:0;padding:0;&[type='number']::-webkit-inner-spin-button,&[type='number']::-webkit-outer-spin-button{-webkit-appearance:none;margin:0;}", " ", " ", ";", ";"], FONT_STYLES, function (props) {
  return themedFontFamily(props.theme.Typography.FontFamily);
}, getDisabledContentStyles, getTextStyles, getSelectStyles, getPlaceholderStyles));

var getBorderRadiusForConnectedWrapper = function getBorderRadiusForConnectedWrapper(props) {
  var borderRadius = props.isRounded || props.theme.Shape.Field === Shape.Pill ? props.size * 2 : props.theme.BorderRadius.FieldsInputConnectedWrapperDefault;

  if (props.side === 'left') {
    return "".concat(borderRadius, "px 0 0 ").concat(borderRadius, "px");
  }

  return "0 ".concat(borderRadius, "px ").concat(borderRadius, "px 0");
};

var InputConnectedWrapper = withTheming(styled.div.withConfig({
  displayName: "Input__InputConnectedWrapper",
  componentId: "sc-1o75rg4-4"
})(["overflow:hidden;border-radius:", ";display:flex;align-items:center;justify-content:space-between;flex-shrink:0;> *{flex:1 0 auto;}&:focus-within{", ";}"], getBorderRadiusForConnectedWrapper, function (_ref3) {
  var theme = _ref3.theme;
  return getFocusStyles({
    theme: theme,
    shouldUseFocusRing: false,
    focusWidth: theme.BorderWidth.FieldsFocus
  });
}));

export { InputConnectedWrapper, InputContainer, InputContent, InputContentContainer, InputRoot };
